import {
  DIGITAL_HUMAN_VERIFICATION_STATUS,
  FACE_VERIFICATION_SESSION_STATE,
  PRIMARY_ASSET_VERIFICATION_STATUS,
} from '@/types/digital-human';
import type {
  DigitalHuman,
  DigitalHumanVerificationResponse,
  DigitalHumanVerificationStatusResponse,
} from '@/types/digital-human';

export type VerificationTone = 'idle' | 'processing' | 'success' | 'failed' | 'expired';

export interface VerificationStepInput {
  verificationStatus?: number | null;
  primaryAssetVerificationStatus?: number | null;
  primaryAssetFailReason?: string | null;
  step?: string | null;
  sessionState?: number | null;
  h5Scanned?: boolean | null;
  isExpired?: boolean | null;
}

export interface VerificationStepSummary {
  tone: VerificationTone;
  title: string;
  description: string;
  progress: number;
  bioLabel: string;
  primaryImageLabel: string;
  terminal: boolean;
}

export function formatPrimaryAssetFailReason(reason?: string | null): string {
  const normalizedReason = reason?.trim();
  if (!normalizedReason) {
    return '主图与人脸认证结果不一致。请更换更清晰、正脸、与本人一致的主图后重新提交。';
  }

  const lowerReason = normalizedReason.toLowerCase();
  if (
    lowerReason.includes('face consistency verification failed') ||
    lowerReason.includes('face consistency')
  ) {
    return '主图与人脸认证结果不一致。请更换本人清晰正脸照片，避免遮挡、侧脸、多人同框或修图过重后重新提交。';
  }

  return normalizedReason;
}

export function hasEnteredPrimaryImageStage(input: VerificationStepInput): boolean {
  if (input.step?.startsWith('primary_image_')) return true;
  if (input.sessionState === FACE_VERIFICATION_SESSION_STATE.SUCCESS) return true;
  return (
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.PROCESSING ||
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.VERIFIED ||
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.FAILED
  );
}

export function getVerificationInput(
  source: DigitalHuman | DigitalHumanVerificationResponse | DigitalHumanVerificationStatusResponse
): VerificationStepInput {
  return {
    verificationStatus: source.verificationStatus,
    primaryAssetVerificationStatus: source.primaryAssetVerificationStatus,
    primaryAssetFailReason: source.primaryAssetFailReason,
    step: 'step' in source ? source.step : undefined,
    sessionState: 'sessionState' in source ? source.sessionState : undefined,
    h5Scanned: 'h5Scanned' in source ? source.h5Scanned : undefined,
    isExpired: 'isExpired' in source ? source.isExpired : undefined,
  };
}

export function resolveVerificationStep(input: VerificationStepInput): VerificationStepSummary {
  if (input.step === 'verification_completed') {
    return {
      tone: 'success',
      title: '真人认证已完成',
      description: '人脸生物认证和主图一致性认证均已通过。',
      progress: 100,
      bioLabel: '已通过',
      primaryImageLabel: '已通过',
      terminal: true,
    };
  }

  if (
    input.verificationStatus === DIGITAL_HUMAN_VERIFICATION_STATUS.VERIFIED &&
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.VERIFIED
  ) {
    return {
      tone: 'success',
      title: '真人认证已完成',
      description: '该真人资产可用于后续生成流程。',
      progress: 100,
      bioLabel: '已通过',
      primaryImageLabel: '已通过',
      terminal: true,
    };
  }

  if (
    input.step === 'primary_image_failed' ||
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.FAILED
  ) {
    return {
      tone: 'failed',
      title: '主图认证失败',
      description: formatPrimaryAssetFailReason(input.primaryAssetFailReason),
      progress: 70,
      bioLabel: '已通过',
      primaryImageLabel: '失败',
      terminal: true,
    };
  }

  if (
    input.step === 'primary_image_processing' ||
    input.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.PROCESSING
  ) {
    return {
      tone: 'processing',
      title: '主图审核中',
      description: '生物认证已通过，正在进行主图一致性审核。',
      progress: 78,
      bioLabel: '已通过',
      primaryImageLabel: '审核中',
      terminal: false,
    };
  }

  if (
    input.step === 'primary_image_required' ||
    input.sessionState === FACE_VERIFICATION_SESSION_STATE.SUCCESS
  ) {
    return {
      tone: 'processing',
      title: '准备主图审核',
      description: '正在创建主图素材并提交一致性认证。',
      progress: 65,
      bioLabel: '已通过',
      primaryImageLabel: '待提交',
      terminal: false,
    };
  }

  if (
    input.step === 'bio_verification_failed' ||
    input.sessionState === FACE_VERIFICATION_SESSION_STATE.FAILED ||
    input.verificationStatus === DIGITAL_HUMAN_VERIFICATION_STATUS.VERIFICATION_FAILED
  ) {
    return {
      tone: 'failed',
      title: '人脸认证失败',
      description: '请重新发起认证，按 H5 页面提示完成真人认证。',
      progress: 30,
      bioLabel: '失败',
      primaryImageLabel: '未开始',
      terminal: true,
    };
  }

  if (
    !hasEnteredPrimaryImageStage(input) &&
    (input.isExpired || input.sessionState === FACE_VERIFICATION_SESSION_STATE.EXPIRED)
  ) {
    return {
      tone: 'expired',
      title: '二维码已过期',
      description: '请重新发起认证生成新的二维码。',
      progress: 20,
      bioLabel: '已过期',
      primaryImageLabel: '未开始',
      terminal: true,
    };
  }

  if (
    input.step === 'bio_verification_processing' ||
    input.sessionState === FACE_VERIFICATION_SESSION_STATE.VERIFYING
  ) {
    return {
      tone: 'processing',
      title: input.h5Scanned ? '人脸认证中' : '等待扫码',
      description: input.h5Scanned
        ? '用户已进入 H5，请等待火山返回认证结果。'
        : '请使用手机扫描二维码进入 H5 认证。',
      progress: input.h5Scanned ? 45 : 25,
      bioLabel: input.h5Scanned ? '认证中' : '待扫码',
      primaryImageLabel: '未开始',
      terminal: false,
    };
  }

  return {
    tone: 'idle',
    title: '未认证',
    description: '上传主图后可提交并发起真人认证。',
    progress: 0,
    bioLabel: '未开始',
    primaryImageLabel: '未开始',
    terminal: false,
  };
}

export function formatVerificationCountdown(expiresAt?: string | null, now = Date.now()): string {
  if (!expiresAt) return '等待二维码';
  const remainMs = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(remainMs) || remainMs <= 0) return '已过期';
  const totalSeconds = Math.ceil(remainMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
